import { z } from 'zod';

import {
  IataSchema,
  HhMmSchema,
  MiceEventPremisesSchema,
  MiceMoneySchema,
  MiceOrderErrorSchema,
} from './mice';
import {
  CarrierSchema,
  FlightNumberSchema,
  MiceCandidateSchema,
  MiceColorSchema,
  MiceDirectionDecisionSchema,
} from './mice_decision';
import { MiceProfileCheckViewSchema } from './mice_profile';

export const MICE_NEED_STATUSES = [
  'validating',
  'invalid',
  'duplicate',
  'ready',
  'recommending',
  'recommended',
  'no_solution',
  'reconsidering',
  'approved',
  'manual',
  'ordered',
] as const;
/** Códigos do erro de uma recomendação que falhou (o agente quebrou ou o projeto sumiu). */
export const RECOMMENDATION_ERROR_CODES = ['recommendation_failed', 'project_missing'] as const;

export const MiceNeedStatusSchema = z.enum(MICE_NEED_STATUSES);
export type MiceNeedStatus = z.infer<typeof MiceNeedStatusSchema>;

export const MiceTripKindSchema = z.enum(['round_trip', 'outbound_only', 'return_only']);
export type MiceTripKind = z.infer<typeof MiceTripKindSchema>;

/** Voo sugerido pela agência na planilha (campos opcionais do que ela informou). */
export const MiceSuggestedFlightSchema = z.object({
  carrier: CarrierSchema,
  flightNumber: FlightNumberSchema,
  date: z.iso.date(),
  from: IataSchema.nullable(),
  to: IataSchema.nullable(),
  departTime: HhMmSchema.nullable(),
});
export type MiceSuggestedFlight = z.infer<typeof MiceSuggestedFlightSchema>;

export const MiceChosenFlightSchema = MiceCandidateSchema.extend({
  fareName: z.string().nullable(),
  farePrice: MiceMoneySchema,
});
export type MiceChosenFlight = z.infer<typeof MiceChosenFlightSchema>;

/** Voo comparado e preterido pelo agente: dados da busca + o porquê (curto, sem valores). */
export const MicePassedOverFlightSchema = MiceCandidateSchema.extend({ why: z.string() });
export type MicePassedOverFlight = z.infer<typeof MicePassedOverFlightSchema>;

/** Log da análise de UM sentido: o que a busca trouxe, o que as premissas cortaram, o que o
 *  agente comparou (contagens e faixa de preço) e os voos que perderam para o escolhido. */
export const MiceDirectionAnalysisSchema = z.object({
  direction: z.enum(['OUTWARD', 'RETURN']),
  found: z.number().int().nonnegative(),
  discarded: z.number().int().nonnegative(),
  discardReasons: z.array(z.string()),
  analyzed: z.number().int().nonnegative(),
  ideal: z.number().int().nonnegative(),
  priceRange: z.object({ min: MiceMoneySchema, max: MiceMoneySchema }).nullable(),
  passedOver: z.array(MicePassedOverFlightSchema),
});
export type MiceDirectionAnalysis = z.infer<typeof MiceDirectionAnalysisSchema>;

export const MICE_NEED_ALERT_CODES = [
  'changed_from_suggestion',
  'savings_review',
  'tolerance_used',
  'suggestion_not_found',
  'suggestion_breaks_premise',
  'existing_voucher',
  'out_of_scope_request',
  'travel_together',
  'baggage_unknown',
  'price_drift',
  'flight_unavailable',
  'direction_already_ordered',
  'airport_outside_sheet',
  'event_airport_changed',
  'airport_request_unmet',
] as const;
export const MiceNeedAlertSchema = z.object({
  code: z.enum(MICE_NEED_ALERT_CODES),
  message: z.string(),
});
export type MiceNeedAlert = z.infer<typeof MiceNeedAlertSchema>;

export const MiceRecommendationSchema = z.object({
  version: z.number().int().min(1),
  createdAt: z.iso.datetime(),
  outbound: MiceChosenFlightSchema.nullable(),
  inbound: MiceChosenFlightSchema.nullable(),
  total: MiceMoneySchema.nullable(),
  reason: z.string(),
  /** Recomendações anteriores ao log de análise não o têm. */
  analysis: z.array(MiceDirectionAnalysisSchema).default([]),
  /** Decisão e cor por sentido; recomendações anteriores a elas não as têm. */
  directions: z.array(MiceDirectionDecisionSchema).default([]),
  color: MiceColorSchema.nullable().default(null),
  alerts: z.array(MiceNeedAlertSchema),
  noSolution: z.string().nullable(),
  comment: z.string().nullable(),
});
export type MiceRecommendation = z.infer<typeof MiceRecommendationSchema>;

export const MiceNeedViewSchema = z.object({
  id: z.string(),
  rowNumber: z.number().int().positive(),
  travelerEmail: z.string(),
  travelerName: z.string().nullable(),
  tripKind: MiceTripKindSchema,
  outboundOrigins: z.array(IataSchema),
  returnDestinations: z.array(IataSchema),
  outboundDate: z.iso.date().nullable(),
  returnDate: z.iso.date().nullable(),
  preference: z.string(),
  agencyNote: z.string(),
  suggestion: z.object({
    outbound: MiceSuggestedFlightSchema.nullable(),
    inbound: MiceSuggestedFlightSchema.nullable(),
  }),
  status: MiceNeedStatusSchema,
  error: MiceOrderErrorSchema.nullable(),
  recommendation: MiceRecommendationSchema.nullable(),
  history: z.array(MiceRecommendationSchema),
  orderIds: z.array(z.string()),
  /** `null`: a validação do cadastro ainda não rodou (ou o documento é anterior a ela). */
  profileCheck: MiceProfileCheckViewSchema.nullable(),
  updatedAt: z.iso.datetime(),
});
export type MiceNeedView = z.infer<typeof MiceNeedViewSchema>;

export const MiceNeedsQuerySchema = z.object({
  status: MiceNeedStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type MiceNeedsQuery = z.infer<typeof MiceNeedsQuerySchema>;

export const MiceNeedPageSchema = z.object({
  items: z.array(MiceNeedViewSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type MiceNeedPage = z.infer<typeof MiceNeedPageSchema>;

export const MiceNeedIdsRequestSchema = z.object({
  needIds: z.array(z.string().min(1)).min(1).max(200),
});
export type MiceNeedIdsRequest = z.infer<typeof MiceNeedIdsRequestSchema>;

/**
 * Comprar aprovadas; `skip` tira um sentido de uma necessidade (ex.: o voucher que já existe).
 * `allGreen` compra todas as verdes (recomendadas ou aprovadas) do evento, sem lista de ids.
 */
export const MiceNeedsOrderRequestSchema = MiceNeedIdsRequestSchema.partial({ needIds: true })
  .extend({
    allGreen: z.boolean().default(false),
    skip: z
      .array(z.object({ needId: z.string().min(1), direction: z.enum(['OUTWARD', 'RETURN']) }))
      .max(400)
      .default([]),
  })
  .refine((b) => b.allGreen || (b.needIds?.length ?? 0) > 0, {
    message: 'Informe as necessidades ou peça todas as verdes',
    params: { code: 'order_target_missing' },
  });
export type MiceNeedsOrderRequest = z.infer<typeof MiceNeedsOrderRequestSchema>;

export const MiceRecommendRequestSchema = z.object({
  needIds: z.array(z.string().min(1)).min(1).max(10),
});
export type MiceRecommendRequest = z.infer<typeof MiceRecommendRequestSchema>;

export const MiceReconsiderRequestSchema = z.object({
  comment: z.string().trim().min(1).max(500),
});
export type MiceReconsiderRequest = z.infer<typeof MiceReconsiderRequestSchema>;

/**
 * Escolha de voos da versão vigente, um por sentido, numa chamada só: a primeira já põe a
 * necessidade em `reconsidering`, então uma segunda chamada daria 409. O worker cota e gera a versão
 * seguinte; `approve` já a aprova.
 */
export const MiceChooseRequestSchema = z
  .object({
    choices: z
      .array(
        z.object({
          direction: z.enum(['OUTWARD', 'RETURN']),
          /** Id de um voo da versão vigente: preterido, `requested` ou `eventWindowOption`; com
           *  `source: 'event_list'`, um voo da lista da faixa do evento (`GET .../flight-options`). */
          candidateId: z.string().min(1),
          source: z.enum(['suggestion', 'event_list']).default('suggestion'),
        }),
      )
      .min(1)
      .max(2),
    /** Versão que o operador viu: os ids são posicionais por busca, então outra versão é 409. */
    version: z.number().int().positive(),
    approve: z.boolean().default(false),
  })
  .refine((b) => new Set(b.choices.map((c) => c.direction)).size === b.choices.length, {
    message: 'Um voo por sentido',
    params: { code: 'duplicate_direction' },
  });
export type MiceChooseRequest = z.infer<typeof MiceChooseRequestSchema>;
/** O que o cliente envia: `approve` é opcional (falso por padrão). */
export type MiceChooseInput = z.input<typeof MiceChooseRequestSchema>;

/**
 * Nova data de um sentido, escolhida pelo operador (o viajante precisa de outro dia). Como na
 * escolha de voos, `version` é a versão vigente que ele viu: outra versão no meio é 409. A
 * necessidade volta a `reconsidering` e o agente busca na data nova.
 */
export const MiceChangeDateRequestSchema = z.object({
  direction: z.enum(['OUTWARD', 'RETURN']),
  date: z.iso.date(),
  version: z.number().int().positive(),
});
export type MiceChangeDateRequest = z.infer<typeof MiceChangeDateRequestSchema>;

export const MiceFlightOptionsQuerySchema = z.object({ direction: z.enum(['OUTWARD', 'RETURN']) });
export type MiceFlightOptionsQuery = z.infer<typeof MiceFlightOptionsQuerySchema>;

/** Por que um voo da lista da faixa do evento não equivale ao do agente: outro aeroporto do evento,
 *  fora do aeroporto que o viajante pediu, aeroporto de último recurso ou fora das regras do evento
 *  (madrugada, pernoite, troca de aeroporto: só no sentido em que todos caíram nelas, #5917). */
export const MiceEventWindowNoteSchema = z.enum([
  'other_event_airport',
  'outside_requested_airport',
  'last_resort_airport',
  'breaks_event_rules',
]);
export type MiceEventWindowNote = z.infer<typeof MiceEventWindowNoteSchema>;

/** Voo da lista da faixa do evento; `notes` vazio = equivalente ao do agente (as listas antigas
 *  não têm marcação). `rulesBroken` = as regras do evento que ele fere (com `breaks_event_rules`). */
export const MiceEventWindowFlightSchema = MiceCandidateSchema.extend({
  notes: z.array(MiceEventWindowNoteSchema).default([]),
  rulesBroken: z.array(z.string()).default([]),
});
export type MiceEventWindowFlight = z.infer<typeof MiceEventWindowFlightSchema>;

/** Os voos da busca da versão vigente dentro da faixa do evento, os equivalentes ao do agente antes
 *  dos marcados, cada grupo do mais barato ao mais caro: o operador escolhe um com
 *  `source: 'event_list'` na mesma versão. */
export const MiceFlightOptionsSchema = z.object({
  version: z.number().int().positive(),
  flights: z.array(MiceEventWindowFlightSchema),
});
export type MiceFlightOptions = z.infer<typeof MiceFlightOptionsSchema>;

/** `POST /needs/order`: pedidos criados e aprovadas puladas porque a importação direta já atende. */
export const MiceNeedsOrderResultSchema = z.object({
  ordered: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  /** "Emitir os verdes" vai em lotes: quantas verdes ainda faltam depois deste (0 fora do lote). */
  remaining: z.number().int().nonnegative().default(0),
});
export type MiceNeedsOrderResult = z.infer<typeof MiceNeedsOrderResultSchema>;

export const MicePremisesRequestSchema = z.object({
  premisesText: z.string().max(5000),
  eventDate: z.iso.date(),
  premises: MiceEventPremisesSchema,
  /** O operador conferiu as premissas com o texto do cliente; salvar sem conferir desconfere. */
  checked: z.boolean().default(false),
});
export type MicePremisesRequest = z.infer<typeof MicePremisesRequestSchema>;

export const MicePremisesExtractRequestSchema = z.object({
  premisesText: z.string().trim().min(1).max(5000),
});
export type MicePremisesExtractRequest = z.infer<typeof MicePremisesExtractRequestSchema>;

/** Os três formatos que a importação única reconhece pelo cabeçalho. */
export const MiceSheetFormatSchema = z.enum(['malha', 'needs_model', 'orders']);
export type MiceSheetFormat = z.infer<typeof MiceSheetFormatSchema>;

/** Prévia da importação: nada gravado ainda; `token` confirma em até 30 min. */
export const MiceImportPreviewSchema = z.object({
  token: z.string(),
  format: MiceSheetFormatSchema,
  fileName: z.string(),
  rows: z.number().int(),
  valid: z.number().int(),
  invalid: z.number().int(),
  duplicate: z.number().int(),
  eventDate: z.iso.date().nullable(),
  projectEventDate: z.iso.date().nullable(),
  problems: z
    .array(
      z.object({
        rowNumber: z.number().int(),
        email: z.string().nullable(),
        message: z.string(),
      }),
    )
    .max(50),
});
export type MiceImportPreview = z.infer<typeof MiceImportPreviewSchema>;

export const MiceImportResultSchema = z.object({
  format: MiceSheetFormatSchema,
  inserted: z.number().int(),
  invalid: z.number().int(),
  duplicate: z.number().int(),
});
export type MiceImportResult = z.infer<typeof MiceImportResultSchema>;
